import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { RouteLocations } from './route-locations.enum';
import { RedirectAfterService } from './redirect-after.service';
import { GameEntityId, GameEntityType } from '../../../../../common/dto/game-entities.dto';
import { GameEntityTemplateId } from '../../../../../common/dto/game-entitie-templates.dto';
import { CampaignId, CampaignPlayerInviteToken, CampaignSessionId } from '../../../../../common/dto/campaigns.dto';
import { ENVIRONMENT } from '../services/injection-tokens';
import { Environment } from '../../../environments/environment.interface';
import { ApplicationComponent } from '../../../../../common/dto/app.dto';

export type NEW_ID_TYPE = 'new';
export const NEW_ID: Readonly<NEW_ID_TYPE> = 'new';
export const QUERY_TRUE: Readonly<string> = '1';

@Injectable()
export class RouteNavigationService {
  constructor(
    private _router: Router,
    private _redirectAfterService: RedirectAfterService,
    @Inject(ENVIRONMENT) private _environment: Environment,
  ) {}

  public async goToDashboardAsync() {
    await this._router.navigateByUrl(RouteLocations.Dashboard.absoluteUrl);
  }

  public async goToRegistrationAsync(redirectToCurrentUrlAfterLogin: boolean = false) {
    if (redirectToCurrentUrlAfterLogin) {
      this._redirectAfterService.saveCurrentUrlForRedirect();
    }

    await this._router.navigateByUrl(
      RouteLocations.Login.getAbsoluteUrlWithQuery({
        [RouteLocations.Login.queryParams.register]: QUERY_TRUE,
      }),
    );
  }

  public async goToLoginAsync(redirectToCurrentUrlAfterLogin: boolean = false) {
    if (redirectToCurrentUrlAfterLogin) {
      this._redirectAfterService.saveCurrentUrlForRedirect();
    }

    await this._router.navigateByUrl(RouteLocations.Login.absoluteUrl);
  }

  public async goToGameEntityListAsync(gameEntityType: GameEntityType, viewGameEntityId?: GameEntityId | undefined) {
    await this._router.navigateByUrl(
      RouteLocations.Dashboard.children.GameEntities.children.GameEntityList.getAbsoluteUrlWithQuery({
        [RouteLocations.Dashboard.children.GameEntities.children.GameEntityList.queryParams.gameEntityType]:
          gameEntityType,
        [RouteLocations.Dashboard.children.GameEntities.children.GameEntityList.queryParams.viewEntity]:
          viewGameEntityId,
      }),
    );
  }

  public async goToGameEntityEditorAsync(
    id: GameEntityId | NEW_ID_TYPE,
    type?: GameEntityType | undefined,
    templateId?: GameEntityTemplateId | undefined,
  ) {
    const queryParams: any = {};

    if (type) {
      queryParams[RouteLocations.Dashboard.children.GameEntities.children.GameEntityEditor.queryParams.type] = type;
    }

    if (templateId) {
      queryParams[RouteLocations.Dashboard.children.GameEntities.children.GameEntityEditor.queryParams.template] =
        templateId;
    }

    await this._router.navigateByUrl(
      RouteLocations.Dashboard.children.GameEntities.children.GameEntityEditor.getAbsoluteUrlWithParams(
        {
          [RouteLocations.Dashboard.children.GameEntities.children.GameEntityEditor.params.gameEntityId]: id,
        },
        queryParams,
      ),
    );
  }

  public async goToCampaignsListAsync() {
    await this._router.navigateByUrl(RouteLocations.Dashboard.children.Campaigns.children.CampaignList.absoluteUrl);
  }

  public async goToCampaignPageAsync(campaignId: CampaignId) {
    await this._router.navigateByUrl(
      RouteLocations.Dashboard.children.Campaigns.children.CampaignPage.getAbsoluteUrlWithParams({
        [RouteLocations.Dashboard.children.Campaigns.children.CampaignPage.params.campaignId]: campaignId,
      }),
    );
  }

  public async goToCampaignSessionPageAsync(campaignId: CampaignId, campaignSessionId: CampaignSessionId) {
    await this._router.navigateByUrl(
      RouteLocations.Dashboard.children.Campaigns.children.CampaignSessionPage.getAbsoluteUrlWithParams({
        [RouteLocations.Dashboard.children.Campaigns.children.CampaignSessionPage.params.campaignId]: campaignId,
        [RouteLocations.Dashboard.children.Campaigns.children.CampaignSessionPage.params.campaignSessionId]:
          campaignSessionId,
      }),
    );
  }

  public getCampaignJoinUrl(campaignId: CampaignId, inviteToken: CampaignPlayerInviteToken): string {
    return this._getFullUrl(
      RouteLocations.Dashboard.children.Campaigns.children.JoinCampaign.getAbsoluteUrlWithQuery({
        [RouteLocations.Dashboard.children.Campaigns.children.JoinCampaign.queryParams.token]: inviteToken,
        [RouteLocations.Dashboard.children.Campaigns.children.JoinCampaign.queryParams.campaign]: campaignId,
      }),
    );
  }

  public async goToNotFoundAsync() {
    await this._router.navigateByUrl(RouteLocations.NotFound.absoluteUrl);
  }

  private _getFullUrl(absoluteUrl: string): string {
    return this._environment.appComponentUrls[ApplicationComponent.App] + absoluteUrl;
  }
}
